import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getGiftAgentWorkspaceUrl, getSessionOpenUrl } from '@/lib/gift-agent';
import { buildTemplateCatalogApiPath, getTemplateIconClassName, getTemplateIconComponent, getTemplateIconStyleAttribute } from '@/lib/template-catalog';
import type { Project, ProjectTemplate, Session } from '@claude-code-api/shared';
import {
  Button,
  Badge,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { StartSessionModal } from '@/components/StartSessionModal';
import { ProjectOverviewTab } from '@/components/features/projects/ProjectOverviewTab';
import { ProjectSessionsTab } from '@/components/features/projects/ProjectSessionsTab';
import { ProjectDatabaseTab } from '@/components/features/projects/ProjectDatabaseTab';
import { ProjectDeploymentsTab } from '@/components/features/projects/ProjectDeploymentsTab';
import { ProjectSettingsTab } from '@/components/features/projects/ProjectSettingsTab';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Play,
  AlertTriangle,
  FolderGit2,
  Folder,
  Users,
} from 'lucide-react';

export default function ProjectPage() {
  const { t, i18n } = useTranslation('projects');
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [sessionModalProjectId, setSessionModalProjectId] = useState<string | null>(null);

  useEffect(() => {
    fetchProject();
    fetchTemplates();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const [projectRes, sessionsRes] = await Promise.all([
        fetch(`/api/projects/${projectId}`, { credentials: 'include' }),
        fetch(`/api/sessions?projectId=${projectId}`, { credentials: 'include' }),
      ]);
      if (projectRes.ok) {
        const data = await projectRes.json();
        setProject(data.data);
      }
      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        setSessions(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch project:', error);
      toast.error(t('detail.errors.loadProject'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(buildTemplateCatalogApiPath('/api/templates?includeDisabled=1', i18n.resolvedLanguage || i18n.language), { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat(i18n.language, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Spinner className="h-12 w-12 mx-auto" />
          <p className="mt-4 text-sm text-muted-foreground">{t('detail.loading')}</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
          <AlertTriangle className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium mb-1">{t('detail.notFoundTitle')}</h3>
        <p className="text-sm text-muted-foreground mb-4">{t('detail.notFoundDescription')}</p>
        <Button asChild>
          <Link to="/">{t('detail.goDashboard')}</Link>
        </Button>
      </div>
    );
  }

  const template = templates.find(t => t.id === project.templateId);
  const activeSession = sessions.find(s => s.status === 'running');
  const TemplateIcon = getTemplateIconComponent(template);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Project Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3 sm:gap-4">
          <Button asChild variant="ghost" size="icon" className="mt-1" aria-label={t('detail.goBack')}>
            <Link to="/"><ArrowLeft className="w-5 h-5" /></Link>
          </Button>
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${template
              ? getTemplateIconClassName(template)
              : project.gitUrl
                ? 'bg-foreground text-background'
                : 'bg-muted text-muted-foreground'
            }`} style={template ? getTemplateIconStyleAttribute(template) : undefined}>
            {project.gitUrl ? (
              <FolderGit2 className="w-7 h-7" />
            ) : template ? (
              <TemplateIcon className="w-7 h-7" />
            ) : (
              <Folder className="w-7 h-7" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="break-words text-2xl font-heading font-bold">{project.name}</h1>
              {activeSession && (
                <Badge variant="running" className="gap-1.5">
                  <StatusIndicator status="running" size="sm" />
                  {t('detail.running')}
                </Badge>
              )}
              {project.isShared && (
                <Badge variant="secondary" className="gap-1.5">
                  <Users className="w-3 h-3" />
                  {project.ownerName
                    ? t('detail.sharedWithYouBy', { owner: project.ownerName })
                    : t('detail.sharedWithYou')}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {t('detail.created', { date: formatDate(project.createdAt) })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 self-end sm:self-auto">
          {activeSession ? (
            <Button asChild variant="outline" className="text-success border-success/30 bg-success/10 hover:bg-success/20 cursor-pointer transition-all">
              <a
                href={(activeSession.runnerType === 'acpx' || activeSession.runnerType === 'acpx-experimental')
                  ? getGiftAgentWorkspaceUrl(activeSession.id)
                  : getSessionOpenUrl(activeSession.id)}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  navigate(`/sessions/${activeSession.id}`);
                }}
              >
                <StatusIndicator status="running" size="sm" className="mr-2" />
                {t('detail.openActiveSession')}
              </a>
            </Button>
          ) : (
            <Button onClick={() => setSessionModalProjectId(projectId!)} className="gap-2">
              <Play className="w-4 h-4" />
              {t('detail.startSession')}
            </Button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <Tabs defaultValue="overview">
        <div className="max-w-full overflow-x-auto overflow-y-hidden">
          <TabsList className="w-max min-w-full justify-start">
            <TabsTrigger value="overview">{t('detail.tabs.overview')}</TabsTrigger>
            <TabsTrigger value="sessions">{t('detail.tabs.sessions')}</TabsTrigger>
            <TabsTrigger value="database">{t('detail.tabs.database')}</TabsTrigger>
            <TabsTrigger value="deployments">{t('detail.tabs.deployments')}</TabsTrigger>
            {!project.isShared && <TabsTrigger value="settings">{t('detail.tabs.settings')}</TabsTrigger>}
          </TabsList>
        </div>
        <TabsContent value="overview" className="mt-6">
          <ProjectOverviewTab
            project={project}
            template={template}
            sessionsCount={sessions.length}
            activeSessionId={activeSession?.id}
          />
        </TabsContent>
        <TabsContent value="sessions" className="mt-6">
          <ProjectSessionsTab projectId={project.id} />
        </TabsContent>
        <TabsContent value="database" className="mt-6">
          <ProjectDatabaseTab project={project} />
        </TabsContent>
        <TabsContent value="deployments" className="mt-6">
          <ProjectDeploymentsTab project={project} />
        </TabsContent>
        {!project.isShared && (
          <TabsContent value="settings" className="mt-6">
            <ProjectSettingsTab project={project} onProjectDeleted={() => navigate('/projects')} />
          </TabsContent>
        )}
      </Tabs>

      {/* Start Session Modal */}
      <StartSessionModal
        projectId={sessionModalProjectId}
        onClose={() => setSessionModalProjectId(null)}
        onSessionCreated={() => fetchProject()}
      />
    </div>
  );
}
